'use client';

import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useEditClientModals } from '@/app/(main)/client-detail/context/EditClientModalsProvider';
import { BadgeSelect } from '@/app/(main)/clients/create/components/BadgeSelect';
import { StayAccommodationTypesField } from '@/app/(main)/clients/create/components/StayAccommodationTypesField';
import Tabs from '@/app/components/Tabs';
import { Input } from '@/app/components/form/Input';
import { TextArea } from '@/app/components/form/TextArea';
import {
  DRINKING_PREFERENCE_OPTIONS,
  INTEREST_PREFERENCE_OPTIONS,
  PILLOW_PREFERENCE_OPTIONS,
  ROOM_PREFERENCE_OPTIONS,
  SPA_PREFERENCE_OPTIONS,
  type DrinkingPreference,
  type InterestPreference,
  type PillowPreference,
  type RoomPreference,
  type SpaPreference,
} from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';
import { MAX_FLIGHT_PREFERENCES_LENGTH } from './const';

export type TravelPreferencesTab = 'travel' | 'stay' | 'lifestyle' | 'personal';

/** Tabs render each item as button wrapped in a tooltip span, so both levels need to stretch. */
const FULL_WIDTH_TABS_ON_MOBILE =
  'max-md:flex max-md:w-full max-md:[&>span]:flex-1 max-md:[&>span>button]:w-full max-md:[&>span>button]:text-center';

const sanitizeInteger = (value: string): string => value.replace(/\D+/g, '');

function ComfortDetailsEdit() {
  const { watch, setValue } = useFormContext();
  const system = watch('personal.personal_metric_system') as string;
  const usHeightRaw = (watch('personal.comfort_related.us.height') as string) || '';
  const usWeight = (watch('personal.comfort_related.us.weight') as string) || '';
  const metricHeight = (watch('personal.comfort_related.metric.height') as string) || '';
  const metricWeight = (watch('personal.comfort_related.metric.weight') as string) || '';

  const [usHeightFt, usHeightIn] = usHeightRaw ? usHeightRaw.split('*') : ['', ''];

  const handleUsHeightFtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ft = sanitizeInteger(e.target.value);
    const combined = ft && usHeightIn ? `${ft}*${usHeightIn}` : ft ? `${ft}*` : usHeightIn ? `*${usHeightIn}` : '';
    setValue('personal.comfort_related.us.height', combined, { shouldValidate: true });
  };

  const handleUsHeightInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inches = sanitizeInteger(e.target.value);
    const combined =
      usHeightFt && inches ? `${usHeightFt}*${inches}` : usHeightFt ? `${usHeightFt}*` : inches ? `*${inches}` : '';
    setValue('personal.comfort_related.us.height', combined, { shouldValidate: true });
  };

  const handleUsWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('personal.comfort_related.us.weight', sanitizeInteger(e.target.value), { shouldValidate: true });
  };

  const handleMetricHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('personal.comfort_related.metric.height', sanitizeInteger(e.target.value), { shouldValidate: true });
  };

  const handleMetricWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('personal.comfort_related.metric.weight', sanitizeInteger(e.target.value), { shouldValidate: true });
  };

  const helperText =
    'Used only when helpful for specific adventure activities (e.g., required for African safaris, certain small airlines/aircraft, biking excursions, scuba diving, etc.)';

  return (
    <div className={cn('rounded-lg bg-white')}>
      <div className="mb-3 flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-3">
        <p className="text-black font-semibold font-inter text-lg leading-normal max-md:text-base">
          Comfort-related Details
        </p>
        <Tabs
          tabs={[
            { id: 'us', label: 'US' },
            { id: 'metric', label: 'Metric' },
          ]}
          activeTab={system}
          onTabChange={(id) => setValue('personal.personal_metric_system', id, { shouldValidate: true })}
          intent="default"
          className={FULL_WIDTH_TABS_ON_MOBILE}
        />
      </div>

      {system === 'metric' && (
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Input
            name="personal.comfort_related.metric.height"
            label="Height"
            suffix="cm"
            placeholder="—"
            value={metricHeight}
            onChange={handleMetricHeightChange}
            intent="primaryWhite"
          />
          <Input
            name="personal.comfort_related.metric.weight"
            label="Weight"
            suffix="kg"
            placeholder="—"
            value={metricWeight}
            onChange={handleMetricWeightChange}
            intent="primaryWhite"
          />
        </div>
      )}

      {system === 'us' && (
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <div className="flex flex-col gap-2">
            <label className="text-body-small text-blue-900">Height</label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                name="personal_comfort_us_ft"
                suffix="ft"
                placeholder="—"
                value={usHeightFt}
                onChange={handleUsHeightFtChange}
                intent="primaryWhite"
              />
              <Input
                name="personal_comfort_us_in"
                suffix="in"
                placeholder="—"
                value={usHeightIn}
                onChange={handleUsHeightInChange}
                intent="primaryWhite"
              />
            </div>
          </div>
          <Input
            name="personal.comfort_related.us.weight"
            label="Weight"
            placeholder="—"
            suffix="lb"
            value={usWeight}
            onChange={handleUsWeightChange}
            intent="primaryWhite"
          />
        </div>
      )}

      <p className="mt-2 text-body-small text-grey-700">{helperText}</p>
    </div>
  );
}

export function EditTravelPreferencesByCategoryForm() {
  const { modalOptions } = useEditClientModals();
  const [activeTab, setTab] = useState<TravelPreferencesTab>(
    () => modalOptions?.travelPreferencesByCategoryDefaultTab ?? 'travel'
  );
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const tabs = [
    { id: 'travel' as const, label: 'Travel' },
    { id: 'stay' as const, label: 'Stay' },
    { id: 'lifestyle' as const, label: 'Lifestyle' },
    { id: 'personal' as const, label: 'Personal' },
  ];

  return (
    <div>
      <div className="mb-4">
        <Tabs<TravelPreferencesTab>
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setTab(id)}
          intent="third"
        />
      </div>

      <div className="mt-4">
        {activeTab === 'travel' && (
          <div>
            <TextArea
              {...register('travel')}
              label="Additional flight preferences"
              disableResize
              placeholder="Please let us know any flight preferences that may be important for booking, including airlines, seating, cabin class, or special requests."
              rows={6}
              size="flexible_height"
              errors={errors}
              value={watch('travel') ?? ''}
              maxLength={MAX_FLIGHT_PREFERENCES_LENGTH}
              errorMessagePosition="relative"
            />
          </div>
        )}

        {activeTab === 'stay' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
              <h5 className="font-sans text-lg font-semibold leading-normal">Accommodation Preferences</h5>
              <p className="text-body-regular text-grey-700">
                While we know every trip is unique, please indicate your favorite types of accommodations by selecting
                up to three categories.
              </p>
            </div>
            <Controller
              name="stay.accommodation_types"
              control={control}
              render={({ field }) => <StayAccommodationTypesField value={field.value} onChange={field.onChange} />}
            />
            <div>
              <TextArea
                {...register('stay.preferences_note')}
                disableResize
                label="Additional"
                placeholder="What else should we know? Please share anything relevant to your ideal trip. If you could tell us about some of your favorite stays of all time, we're better able to match you with similar types of stays in the future."
                rows={3}
                size="flexible_height"
                errors={errors}
                value={watch('stay.preferences_note') ?? ''}
                maxLength={MAX_FLIGHT_PREFERENCES_LENGTH}
                errorMessagePosition="relative"
              />
            </div>
          </div>
        )}

        {activeTab === 'lifestyle' && (
          <div className="rounded-[14px] flex flex-col gap-4 pb-[30px]">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">General Interests</h5>
                <p className="font-sans text-base font-normal leading-6 text-[#495057]">
                  What are your key priorities when traveling? Select up to three.
                </p>
              </div>
              <Controller
                name="general_interests"
                control={control}
                render={({ field }) => (
                  <BadgeSelect<InterestPreference>
                    label=""
                    showMoreButtonSize="large"
                    type="secondary"
                    options={INTEREST_PREFERENCE_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    visibleCount={10}
                    collapsible
                  />
                )}
              />
            </div>

            <div className="h-px bg-[#D9E2F3]" />

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Beverage Preferences</h5>
                <p className="font-sans text-base font-normal leading-6 text-[#495057]">
                  While we know your beverage preferences can always depend on mood and time of day, what is your
                  favorite type of beverage to receive as a welcome amenity where available?
                </p>
              </div>
              <Controller
                name="beverage_preferences"
                control={control}
                render={({ field }) => (
                  <BadgeSelect<DrinkingPreference>
                    label=""
                    type="secondary"
                    options={DRINKING_PREFERENCE_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="h-px bg-[#D9E2F3]" />

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Pillow Preferences</h5>
              </div>
              <Controller
                name="pillow_preferences"
                control={control}
                render={({ field }) => (
                  <BadgeSelect<PillowPreference>
                    label=""
                    type="secondary"
                    showMoreButtonSize="large"
                    options={PILLOW_PREFERENCE_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="h-px bg-[#D9E2F3]" />

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">
                  Room Configuration Preferences
                </h5>
              </div>
              <Controller
                name="room_configuration_preferences"
                control={control}
                render={({ field }) => (
                  <BadgeSelect<RoomPreference>
                    label=""
                    type="secondary"
                    showMoreButtonSize="large"
                    options={ROOM_PREFERENCE_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="h-px bg-[#D9E2F3]" />

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">Spa Preferences</h5>
              </div>
              <Controller
                name="spa_preferences"
                control={control}
                render={({ field }) => (
                  <BadgeSelect<SpaPreference>
                    label=""
                    type="secondary"
                    showMoreButtonSize="large"
                    options={SPA_PREFERENCE_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
              <TextArea
                {...register('spa_preferences_special_requests')}
                disableResize
                placeholder="If you're an spa enthusiast, anything we should know like your preferred spa appointment time allows us to best make bookings on your behalf as requested."
                rows={6}
                size="flexible_height"
                errors={errors}
                value={watch('spa_preferences_special_requests') ?? ''}
                maxLength={MAX_FLIGHT_PREFERENCES_LENGTH}
                errorMessagePosition="relative"
              />
            </div>

            <div className="h-px bg-[#D9E2F3]" />

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <h5 className="font-sans text-lg font-semibold leading-normal text-[#212529]">
                  General Likes & Dislikes
                </h5>
              </div>
              <TextArea
                {...register('likes')}
                disableResize
                label=""
                size="flexible_height"
                rows={8}
                errors={errors}
                placeholder="As we craft your trip, any and all unique likes and dislikes are helpful. Please use this box to share what a perfect stay looks like to you! Any turndown preferences? Things you like to have in your room on arrival like a steamer or extra hangers? The more specific, the better."
                value={watch('likes') ?? ''}
                maxLength={MAX_FLIGHT_PREFERENCES_LENGTH}
                errorMessagePosition="relative"
              />
            </div>
          </div>
        )}

        {activeTab === 'personal' && (
          <div className="flex flex-col gap-4 max-h-[445px] max-md:max-h-none">
            <div className="rounded-[14px] flex flex-col gap-4">
              <ComfortDetailsEdit />
              <div>
                <TextArea
                  {...register('personal.information')}
                  rows={8}
                  size="flexible_height"
                  placeholder="Please share any personal information you'd like us to be aware of that may help us better support you or this traveler. This may include preferences, sensitivities, accessibility needs, or any accommodations that would help ensure a comfortable travel experience."
                  value={watch('personal.information') ?? ''}
                  errors={errors}
                  disableResize
                  maxLength={MAX_FLIGHT_PREFERENCES_LENGTH}
                  errorMessagePosition="relative"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
